
/**
 * Step 3: 使用场景推荐
 */

import React from 'react';
import { Card, Button, Space, Empty, Spin, Row, Col, Typography, Badge } from 'antd';
import { EnvironmentOutlined, RocketOutlined } from '@ant-design/icons';
import type { UsageScenario } from '../types';
import { ScenarioCard, SiteSelectorCard } from './common';
import { useScenarioStep } from './hooks/useScenarioStep';

const { Text } = Typography;

interface ScenarioStepProps {
    scenarios: UsageScenario[];
    onSelect: (selectedIds: string[]) => void;
    onNext: (targetSites: string[]) => void;
    onBack: () => void;
    onRegenerate?: () => void;
    loading?: boolean;
    submitting?: boolean;
    keywordsBySite?: Record<string, string>;
}

export const ScenarioStep: React.FC<ScenarioStepProps> = ({
    scenarios,
    onSelect,
    onNext,
    onBack,
    onRegenerate,
    loading,
    submitting,
    keywordsBySite = {},
}) => {
    const {
        selectedIds,
        targetSites,
        setTargetSites,
        handleToggle,
        handleSelectAll,
        handleQuickSelect
    } = useScenarioStep({
        scenarios,
        onSelect,
        keywordsBySite
    });

    return (
        <div className="scenario-step">
            <Card
                title={
                    <Space>
                        <EnvironmentOutlined />
                        使用场景推荐
                        {scenarios.length > 0 && (
                            <Badge count={scenarios.length} style={{ backgroundColor: '#52c41a' }} />
                        )}
                    </Space>
                }
                bordered={false}
                extra={
                    <Space>
                        {onRegenerate && scenarios.length > 0 && (
                            <Button size="small" onClick={onRegenerate} loading={loading}>
                                重新推荐场景
                            </Button>
                        )}
                        {scenarios.length > 0 && (
                            <>
                                <Text type="secondary">
                                    已选 {selectedIds.length}/{scenarios.length}
                                </Text>
                                <Button size="small" onClick={handleQuickSelect}>
                                    快速选择前5个
                                </Button>
                                <Button size="small" onClick={handleSelectAll}>
                                    {selectedIds.length === scenarios.length ? '取消全选' : '全选'}
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                {/* 场景列表 */}
                {loading && scenarios.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 48 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">AI 正在根据目标人群推荐使用场景...</Text>
                        </div>
                    </div>
                ) : scenarios.length === 0 ? (
                    <Empty description="暂无场景数据，请先选择目标人群" />
                ) : (
                    <>
                        <Row gutter={[12, 12]}>
                            {scenarios.map(scenario => (
                                <Col key={scenario.id} xs={24} sm={12} lg={8}>
                                    <ScenarioCard
                                        scenario={scenario}
                                        selected={scenario.selected || false}
                                        onToggle={() => handleToggle(scenario.id)}
                                    />
                                </Col>
                            ))}

                            {/* 如果仍在加载，显示加载占位符 */}
                            {loading && (
                                <Col xs={24} sm={12} lg={8}>
                                    <Card
                                        style={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: 200,
                                            background: '#f5f5f5',
                                            border: '1px dashed #d9d9d9'
                                        }}
                                        bordered={false}
                                    >
                                        <div style={{ textAlign: 'center' }}>
                                            <Spin />
                                            <div style={{ marginTop: 8 }}>
                                                <Text type="secondary">正在生成更多...</Text>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    </>
                )}
            </Card>

            {/* 目标站点选择 */}
            <SiteSelectorCard
                targetSites={targetSites}
                onSitesChange={setTargetSites}
                keywordsBySite={keywordsBySite}
            />

            {/* 底部操作栏 */}
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
                <Button size="large" onClick={onBack}>
                    上一步
                </Button>
                <Button
                    type="primary"
                    size="large"
                    loading={submitting}
                    onClick={() => onNext(targetSites)}
                    disabled={selectedIds.length === 0 || targetSites.length === 0}
                    icon={<RocketOutlined />}
                >
                    生成文案 ({selectedIds.length} 场景 × {targetSites.length} 站点)
                </Button>
            </div>
        </div>
    );
};

export default ScenarioStep;
